<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-body pb-10 pt-10 page-container">
          <b-form @submit.stop.prevent="onSubmit()">
            <b-row v-if="preview.logo_image" class="mb-5">
              <b-col sm="12" class="text-center mb-5">
                <img
                  :src="preview.logo_image"
                  alt="Logo image"
                  class="logo-image-preview"
                />
              </b-col>
            </b-row>
            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.DEFAULT.FORM.FIELDS.NAME") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-input
                  id="name-input"
                  name="name-input"
                  v-model="$v.form.name.$model"
                  :state="validateState('name')"
                  aria-describedby="name-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="name-live-feedback">
                  <span v-if="!$v.form.name.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-if="!$v.form.name.maxLength" class="error">
                    {{
                      $t("PAGE.DEFAULT.FORM.VALIDATION.MAX_LENGTH", {
                        max: 256
                      })
                    }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.COMPANY.FORM.FIELDS.LOGO_IMAGE") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-file
                  v-model="form.logo_image_file"
                  @change="handleImage"
                  accept="image/*"
                  :placeholder="
                    this.$t('PAGE.DEFAULT.FORM.PLACEHOLDER.CHOOSE_IMAGE')
                  "
                  drop-placeholder="Drop file here..."
                ></b-form-file>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="12">
                <b-button class="float-right" @click.prevent="cancel()">{{
                  $t("PAGE.DEFAULT.FORM.BTN_CANCEL")
                }}</b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="float-right mr-3"
                  >{{ $t("PAGE.DEFAULT.FORM.BTN_SUBMIT") }}</b-button
                >
              </b-col>
            </b-row>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  UPDATE_COMPANY,
  FIND_COMPANY
} from "@/core/services/store/company.module";
import { SET_CURRENT_USER_COMPANY } from "@/core/services/store/auth.module";
import { Config } from "@/core/config/variable.config";

export default {
  name: "Company",
  mixins: [validationMixin],
  data() {
    return {
      preview: {
        logo_image: null,
        logo_image_default: null
      },
      form: {
        name: "",
        logo_image: null,
        logo_image_file: []
      }
    };
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(256)
      }
    }
  },
  computed: {
    ...mapState({
      currentUserData: (state) => state.auth.currentUserData
    })
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: `${this.$t("MENU.COMPANY_MANAGEMENT.COMPANY")}` }
    ]);
    this.getCompany();
  },
  methods: {
    getCompany() {
      this.$store
        .dispatch(FIND_COMPANY, {
          id: this.currentUserData.company.id
        })
        .then(({ data }) => {
          if (data.status === "success" && data.data) {
            this.form.name = data.data.name;

            if (data.data.logo_image) {
              this.preview.logo_image_default = `${Config.ADMIN_IMAGE_URL}/${data.data.logo_image}`;
            } else {
              this.preview.logo_image_default = "media/company/default.png";
            }

            this.setDefaultImage();
          } else if (data.status === "error" && data.error) {
            Swal.fire({
              icon: "error",
              text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
              confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            text: `${error}`,
            confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
          });
        });
    },
    setDefaultImage() {
      this.form.logo_image = null;
      this.form.logo_image_file = [];
      this.preview.logo_image = this.preview.logo_image_default;
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    handleImage(e) {
      const selectImage = e.target.files[0];

      if (selectImage != undefined) {
        this.encodeBase64(selectImage);
      } else {
        this.setDefaultImage();
      }
    },
    encodeBase64(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.form.logo_image = e.target.result;
        this.preview.logo_image = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    onSubmit() {
      this.$v.form.$touch();

      if (this.$v.form.$anyError) {
        return;
      }

      this.updateCompany(this.form, this.currentUserData.company.id);
    },
    updateCompany(data, id) {
      Swal.fire({
        title: `${this.$t("LOADING")}`,
        allowEscapeKey: false,
        allowOutsideClick: false,
        timer: 36000,
        onOpen: () => {
          Swal.showLoading();
          this.$store
            .dispatch(UPDATE_COMPANY, { id, data })
            .then(({ data }) => {
              Swal.close();

              if (data.status === "success" && data.data) {
                Swal.fire({
                  icon: "success",
                  text: this.$t("RESPONSE_MESSAGE.SAVE_DATA_SUCCESS")
                });
                this.$v.$reset();

                if (typeof data.data !== "string") {
                  this.$store.commit(SET_CURRENT_USER_COMPANY, {
                    company: data.data
                  });
                  this.getCompany();
                }
              } else if (data.status === "error" && data.error) {
                Swal.fire({
                  icon: "error",
                  text: this.$t("RESPONSE_MESSAGE." + data.error),
                  confirmButtonText: this.$t("PAGE.DEFAULT.FORM.BTN_OK")
                });
              }
            })
            .catch((error) => {
              Swal.close();
              Swal.fire({
                icon: "error",
                text: error,
                confirmButtonText: this.$t("PAGE.DEFAULT.FORM.BTN_OK")
              });
            });
        }
      });
    },
    cancel() {
      this.$v.$reset();
      this.getCompany();
    }
  }
};
</script>

<style scoped>
.logo-image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  border: solid 1px #ccc;
  border-radius: 100%;
}
</style>
